/**
 * @file MetricTile.tsx
 * @description Плитка показателя для сводок кабинета (phase19.31, шаг О4;
 * commora-console.md §4, §10–11; uiux-design.md §5 «Сводка»).
 *
 * Подпись — обычным регистром (`type-caption`, 13/12px), число —
 * `type-metric` с табличными цифрами (24/28px), под ним пояснение. Плитка с
 * адресом — ссылка ЦЕЛИКОМ и реагирует на наведение мышью по §11 (подъём,
 * мягкая тень, синий край — класс `card-interactive` темы); без адреса —
 * спокойная карточка без реакции. Тон «внимание/опасно» задаёт страница по
 * своему условию (обычно «только при числе больше нуля»).
 *
 * Размер (SC69, phase19.31): `default` — плитка сводки раздела («Обзор
 * склада»), `compact` — плашка итога над списком («Показано / Приход /
 * Расход / Сальдо»): высота ~76px, значение 20px/600. Поверхность всегда
 * непрозрачная — декоративный фон кабинета сквозь плитку не виден.
 *
 * `valueTone` красит только число, подложка остаётся белой: «Приход» —
 * зелёным, «Расход» — янтарным. `tone` (внимание/опасно) меняет плитку целиком.
 *
 * ```tsx
 * <MetricTile
 *   label="Без места"
 *   value={4}
 *   hint="партии без ячейки"
 *   tone={4 > 0 ? "warning" : "default"}
 *   to="/shop/chaiberi/wms/stock?unplaced=1"
 * />
 * <MetricTile size="compact" label="Приход" value="+190 шт" valueTone="success" />
 * ```
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Skeleton } from "./skeleton";
import { cn } from "@/lib/utils";

/** Тон плитки: спокойный, «внимание» (янтарный), «опасно» (красный). */
export type MetricTone = "default" | "warning" | "danger";

/** Размер плитки: сводка раздела или компактная плашка итога над списком. */
export type MetricSize = "default" | "compact";

/** Цвет одного числа на белой плитке. */
export type MetricValueTone = "default" | "success" | "warning" | "danger";

/** Реестр тонов: подложка, линия, цвет числа. Цвета — токены темы. */
const TONE_CLASS: Record<MetricTone, { box: string; value: string }> = {
  default: { box: "bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]", value: "text-on-surface" },
  warning: {
    box: "bg-[var(--warning-soft)] [box-shadow:inset_0_0_0_1px_var(--warning-line)]",
    value: "text-[var(--status-attention-fg)]",
  },
  danger: {
    box: "bg-[var(--destructive-soft)] [box-shadow:inset_0_0_0_1px_var(--destructive-line)]",
    value: "text-[var(--status-error-fg)]",
  },
};

/** Реестр цветов числа (`valueTone`); `default` — цвет по тону плитки. */
const VALUE_TONE_CLASS: Record<Exclude<MetricValueTone, "default">, string> = {
  success: "text-[var(--status-success-fg,var(--success))]",
  warning: "text-[var(--status-attention-fg,var(--warning))]",
  danger: "text-[var(--status-error-fg,var(--destructive-text))]",
};

/**
 * Каркас по размеру — общий у плитки и заготовки, чтобы высота не прыгала.
 * Компактная: ниже и мельче число — через переменную шкалы `--fs-metric`.
 */
const SIZE_CLASS: Record<MetricSize, string> = {
  default: "flex min-h-22 min-w-0 flex-col gap-1 rounded-xl p-3 lg:p-4",
  compact: "flex min-h-[76px] min-w-0 flex-col justify-center gap-0.5 rounded-xl p-3 [--fs-metric:1.25rem]",
};

interface Props {
  label: string;
  value: ReactNode;
  /** Что означает число: «заданий ждут сборки». */
  hint?: string;
  icon?: LucideIcon;
  tone?: MetricTone;
  /** Цвет числа на белой плитке («Приход» — зелёный); тон плитки не меняет. */
  valueTone?: MetricValueTone;
  /** `compact` — плашка итога над списком (~76px, число 20px/600). */
  size?: MetricSize;
  /** Адрес: плитка становится ссылкой целиком. */
  to?: string;
  /** Подпись ссылки для скринридера; по умолчанию «<подпись>: <число> — <пояснение>». */
  ariaLabel?: string;
  /** Подсказка при наведении, когда короткой подписи мало. */
  title?: string;
  className?: string;
}

export function MetricTile({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  valueTone = "default",
  size = "default",
  to,
  ariaLabel,
  title,
  className,
}: Props) {
  const toneCls = TONE_CLASS[tone];
  const valueCls = valueTone === "default" ? toneCls.value : VALUE_TONE_CLASS[valueTone];
  const body = (
    <>
      <span className="type-caption flex min-w-0 items-center gap-1.5 text-on-surface-variant">
        {Icon ? <Icon aria-hidden className="size-4 shrink-0" /> : null}
        <span className="truncate">{label}</span>
      </span>
      <span className={cn("type-metric", valueCls)}>{value}</span>
      {hint ? <span className="type-caption line-clamp-2 text-on-surface-variant">{hint}</span> : null}
    </>
  );
  const cls = cn(SIZE_CLASS[size], toneCls.box, className);

  if (to) {
    const spoken = typeof value === "number" || typeof value === "string" ? String(value) : "";
    const linkLabel = ariaLabel ?? `${label}: ${spoken}${hint ? ` — ${hint}` : ""}`;
    return (
      <Link
        to={to}
        aria-label={linkLabel}
        title={title}
        data-tone={tone}
        data-size={size}
        className={cn(cls, "card-interactive outline-none")}
      >
        {body}
      </Link>
    );
  }
  return (
    <div data-tone={tone} data-size={size} title={title} className={cls}>
      {body}
    </div>
  );
}

/** Заготовка плитки на время загрузки — той же высоты. */
export function MetricTileSkeleton({ className, size = "default" }: { className?: string; size?: MetricSize }) {
  return (
    <div aria-hidden className={cn(SIZE_CLASS[size], "bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]", className)}>
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className={size === "compact" ? "h-5 w-1/3" : "h-7 w-1/3"} />
      {size === "compact" ? null : <Skeleton className="h-3 w-3/4" />}
    </div>
  );
}
